import React, { useState, useEffect } from "react";

/**
 * Shared chrome for the Sales Intake page: category taxonomy,
 * duration/time formatters, the self-ticking live-elapsed timer, and the
 * inline SVG icons. Split out of LiveCalls.js purely for size — the page is
 * the only consumer today, but the taxonomy is the one true list the backend
 * bulk-tag allowlist mirrors (routes/admin/calls.js VALID_TAGS).
 */

// Small reusable close icon — replaces the legacy `&times;` HTML entities so
// the chrome stays visually consistent with the SVG icons elsewhere.
export function CloseIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * A visit the AI receptionist (Hank) booked — the "Booked by Hank" filter.
 * His other rows are call backs on the Missed tab (status "missed"). Same
 * test as the server's (chariot-backend services/voiceAgent/salesIntake.js
 * aiBookingSql).
 */
export const isHankBooking = (call) => Boolean(call?.ai_receptionist) && call.status === "completed";

/** A booking the customer made on the website's form (chariot-backend services/onlineBooking/intake.js) — no call behind it. */
export const isOnlineBooking = (call) => Boolean(call?.online_booking_id);

/** A booking that waits at the top of Pending Review for a manager: Hank's, or one made online. */
export const isPinnedBooking = (call) => isHankBooking(call) || isOnlineBooking(call);

/**
 * Pending Review with the bookings pinned: Hank's and the website's come first — the urgent ones before the rest — and
 * every group keeps the order it was sorted in.
 */
export const pinBookings = (list) => {
  const urgent = (c) => (c.ai_extracted_fields || {}).is_emergency === true;
  const pinned = list.filter(isPinnedBooking);
  return [...pinned.filter(urgent), ...pinned.filter((c) => !urgent(c)), ...list.filter((c) => !isPinnedBooking(c))];
};

/**
 * The NS tag's hover text — "not scheduled", as on Poseidon Live: the visit is booked, but no card is on file yet, so
 * it is not confirmed. Shown where call.card_on_file === false (the lists send null on every other call): Sales Intake's
 * History (Pending Review lists only bookings with a card on file, Oct 28 2026) and Sales Sheets (hank_card_on_file /
 * online_card_on_file).
 */
export const NS_TITLE = "Not scheduled — no card on file yet. Booked by Hank; the visit is confirmed once a card is on file (the customer can still add one from his texted link).";
export const NS_ONLINE_TITLE = "Not scheduled — no card on file yet. Booked online; the visit is confirmed once a card is on file (the customer can still add one from the link in their confirmation email).";
/** The NS hover text for a row: an online booking's, else Hank's. */
export const nsTitle = (call) => (isOnlineBooking(call) ? NS_ONLINE_TITLE : NS_TITLE);

export function fmtDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/** Live elapsed time since a start timestamp. Returns "MM:SS" so it visually
 *  distinguishes "live" from the static "Xm Ys" used for completed calls. */
export function fmtLiveElapsed(startStr) {
  if (!startStr) return "—";
  const elapsed = Math.max(0, Math.floor((Date.now() - new Date(startStr).getTime()) / 1000));
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Self-ticking elapsed timer for ringing/active rows. Owning the 1s interval
 *  here (instead of a page-level tick) means only these tiny spans re-render
 *  each second — not the whole table + detail panel. */
export function LiveElapsed({ startStr, ringing = false }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className={`lc2-live-duration${ringing ? " lc2-live-ringing" : ""}`}>
      {fmtLiveElapsed(startStr)}
    </span>
  );
}

export function fmtTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ── Resizable detail panel ──────────────────────────────────────────────────
// The Pending Review list (.lc2-main) and the call detail panel (.lc2-panel)
// share a flex row; a drag handle between them lets the user rebalance the
// split. Width persists in localStorage with a sensible default.

export const CATEGORY_OPTIONS = [
  { value: "new_sales_lead", label: "Sales Lead" },
  { value: "follow_up", label: "Follow Up" },
  { value: "order_info", label: "Order Information" },
  { value: "payment", label: "Payment" },
  { value: "support", label: "Support" },
  { value: "spam", label: "Spam" },
  { value: "other", label: "Other" },
];

export const TAG_LABELS = {};
CATEGORY_OPTIONS.forEach((c) => { TAG_LABELS[c.value] = c.label; });

export const TAG_COLORS = {
  new_sales_lead: "#22c55e",
  follow_up: "#3b82f6",
  order_info: "#0ea5e9",
  payment: "#f59e0b",
  support: "#8b5cf6",
  spam: "#ef4444",
  other: "#6b7280",
};

// ─── Call outcomes ──────────────────────────────────────────────────────────
// The paper "Inbound Call Verification Log" taxonomy, digitized. Mirrored by
// the backend whitelist (routes/admin/calls.js VALID_OUTCOMES).

export const OUTCOME_OPTIONS = [
  { value: "sold", label: "Sold" },
  // Stored value stays "estimate" (DB, API, AI enum); staff see it as "Pending".
  { value: "estimate", label: "Pending" },
  { value: "lost", label: "Lost" },
  { value: "other", label: "Other" },
];

export const OUTCOME_LABELS = {};
OUTCOME_OPTIONS.forEach((o) => { OUTCOME_LABELS[o.value] = o.label; });

export const OUTCOME_COLORS = {
  sold: "#22c55e",
  estimate: "#3b82f6",
  lost: "#ef4444",
  other: "#6b7280",
};

// Quick-pick reason chips — the form's "Common Reasons" row.
export const OUTCOME_REASONS = [
  "Waiting on Approval",
  "Price Shopping",
  "Out of Area",
  "No Answer / Unable to Reach",
  "Customer Deferred",
  "Competitor",
  "Parts Availability",
];

// ─── Date-range filter (header filter popover + Scorecard tab) ──────────────
// Deliberately NOT persisted anywhere: leaving the page resets to defaults.

// Today first, All time last (Allen, Oct 16 2026); Sales Intake starts on Today (useIntakeFilters FILTER_DEFAULTS).
export const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "all", label: "All time" },
];

// Resolve a {preset, from, to} filter into concrete Date bounds (or nulls).
// Custom from/to are yyyy-mm-dd strings from <input type="date"> — parsed as
// LOCAL dates (new Date("yyyy-mm-dd") would parse as UTC midnight and shift
// the day for anyone west of Greenwich).
export function computeDateRange({ preset, from, to }) {
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
  const parseLocal = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const now = new Date();
  switch (preset) {
    case "today": return { from: startOfDay(now), to: null };
    case "yesterday": {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case "last7": {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      return { from: startOfDay(d), to: null };
    }
    case "last30": {
      const d = new Date(now); d.setDate(d.getDate() - 29);
      return { from: startOfDay(d), to: null };
    }
    case "thisMonth": return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: null };
    case "custom": {
      const f = parseLocal(from);
      const t = parseLocal(to);
      return { from: f ? startOfDay(f) : null, to: t ? endOfDay(t) : null };
    }
    default: return { from: null, to: null };
  }
}

export const fmtRangeDay = (d) =>
  d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "…";


export const InboundIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24.48 24.476" fill="currentColor">
    <path d="M24.149,18.038c-.292-.977-1.291-1.721-3.24-2.413-1.223-.429-2.53-.739-3.407-.935l-.083-.017c-.893-.161-1.917.221-2.526.935-.545.643-.896,1.005-1.095,1.196l-.065.062-.077-.045c-1.217-.702-2.151-1.499-3.352-2.638-1.106-1.164-1.932-2.129-2.646-3.351l-.045-.078.062-.065c.188-.195.546-.542,1.182-1.088.758-.633,1.132-1.681.933-2.61-.284-1.33-.601-2.483-.941-3.426-.685-1.938-1.404-2.903-2.404-3.227-.423-.143-.874-.215-1.338-.215C3.46.123,1.895,1.034.701,2.688.129,3.47-.089,4.601.033,6.146c.093,1.211.398,2.604.904,4.141,1.083,3.213,2.892,6.285,4.84,8.215l.083.088c1.942,2.006,5.056,3.856,8.329,4.947,1.475.498,2.908.811,4.145.907.265.021.525.032.774.032,1.124,0,2.021-.23,2.667-.684,2.123-1.515,3.033-3.719,2.374-5.751v-.003ZM20.692,22.249h0c-.221.157-.675.343-1.583.343-.197,0-.408-.009-.629-.027-1.083-.083-2.359-.365-3.694-.815-3-1-5.831-2.672-7.572-4.472l-.02-.029c-1.775-1.708-3.45-4.536-4.465-7.55-.454-1.377-.736-2.656-.817-3.702-.109-1.39.152-1.979.318-2.207.831-1.149,1.853-1.783,2.878-1.783.269,0,.515.04.751.121.16.049.608.352,1.219,2.077.312.864.605,1.934.871,3.18.06.281-.107.613-.303.773-.844.722-1.416,1.277-1.698,1.648-.352.473-.368,1.163-.04,1.759.836,1.484,1.79,2.609,3.058,3.943,1.358,1.291,2.487,2.247,3.955,3.061.587.331,1.291.318,1.752-.032.377-.281.934-.856,1.657-1.708.181-.212.495-.35.759-.302.839.188,2.078.481,3.191.872,1.78.632,2.041,1.097,2.079,1.224.402,1.237-.22,2.593-1.665,3.624Z"/>
    <polygon points="16.982 8.825 24.48 1.327 23.153 0 15.655 7.498 14.333 6.177 12.881 11.6 18.304 10.147 16.982 8.825"/>
  </svg>
);

export const OutboundIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24.48 24.476" fill="currentColor">
    <path d="M24.149,18.038c-.292-.977-1.291-1.721-3.24-2.413-1.223-.429-2.53-.739-3.407-.935l-.083-.017c-.893-.161-1.917.221-2.526.935-.545.643-.896,1.005-1.095,1.196l-.065.062-.077-.045c-1.217-.702-2.151-1.499-3.352-2.638-1.106-1.164-1.932-2.129-2.646-3.351l-.045-.078.062-.065c.188-.195.546-.542,1.182-1.088.758-.633,1.132-1.681.933-2.61-.284-1.33-.601-2.483-.941-3.426-.685-1.938-1.404-2.903-2.404-3.227-.423-.143-.874-.215-1.338-.215C3.46.123,1.895,1.034.701,2.688.129,3.47-.089,4.601.033,6.146c.093,1.211.398,2.604.904,4.141,1.083,3.213,2.892,6.285,4.84,8.215l.083.088c1.942,2.006,5.056,3.856,8.329,4.947,1.475.498,2.908.811,4.145.907.265.021.525.032.774.032,1.124,0,2.021-.23,2.667-.684,2.123-1.515,3.033-3.719,2.374-5.751v-.003ZM20.692,22.249h0c-.221.157-.675.343-1.583.343-.197,0-.408-.009-.629-.027-1.083-.083-2.359-.365-3.694-.815-3-1-5.831-2.672-7.572-4.472l-.02-.029c-1.775-1.708-3.45-4.536-4.465-7.55-.454-1.377-.736-2.656-.817-3.702-.109-1.39.152-1.979.318-2.207.831-1.149,1.853-1.783,2.878-1.783.269,0,.515.04.751.121.16.049.608.352,1.219,2.077.312.864.605,1.934.871,3.18.06.281-.107.613-.303.773-.844.722-1.416,1.277-1.698,1.648-.352.473-.368,1.163-.04,1.759.836,1.484,1.79,2.609,3.058,3.943,1.358,1.291,2.487,2.247,3.955,3.061.587.331,1.291.318,1.752-.032.377-.281.934-.856,1.657-1.708.181-.212.495-.35.759-.302.839.188,2.078.481,3.191.872,1.78.632,2.041,1.097,2.079,1.224.402,1.237-.22,2.593-1.665,3.624Z"/>
    <polygon points="20.379 2.774 12.881 10.273 14.208 11.6 21.706 4.102 23.027 5.423 24.48 0 19.057 1.453 20.379 2.774"/>
  </svg>
);

// Small filled play triangle — marks a row whose call has a recording to listen to.
export const PlayIcon = ({ size = 12 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

